import Icon from '@atoms/Icon/Icon';
import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import ReturnLink from '@atoms/ReturnLink/ReturnLink';
import Tag from '@atoms/Tag/Tag';
import { IProductDetails } from '@models/IProductDetails';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import AddToCartTray from '@molecules/AddToCartTray/AddToCartTray';
import InfoModal from '@molecules/InfoModal/InfoModal';
import ProductDetailsGallery from '@molecules/ProductDetailsGallery/ProductDetailsGallery';
import ProductDetailsSidePanel from '@molecules/ProductDetailsSidePanel/ProductDetailsSidePanel';
import ProductDetailsStickyBuyBar from '@molecules/ProductDetailsStickyBuyBar/ProductDetailsStickyBuyBar';
import PageBackgroundAnimator from '@organisms/PageBackgroundAnimator/PageBackgroundAnimator';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import useProductPageContentStore from '@state/ProductPageStore';
import useStickyBuyBarStore from '@state/StickyBuyBarStore';
import { getProductData } from '@utils/helpers/getProductData';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useIsClientSide from '@utils/hooks/useIsClientSide';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useProductDetailsAddToCart from '@utils/hooks/useProductDetailsAddToCart';
import useProductDetailsScroll from '@utils/hooks/useProductDetailsScroll';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import useStickyOffset from '@utils/hooks/useStickyOffset';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const ProductDetails = (props: IProductDetails) => {
  const [productDetails, setProductDetails] = useState(props);
  const [isProductDetailsLoading, setIsProductDetailsLoading] = useState(false);
  const [galleryContainerRef, { clientHeight: galleryContainerHeight }] =
    useResizeObserver();
  const { setStickyBuyBarIsInView } = useStickyBuyBarStore((state) => ({
    setStickyBuyBarIsInView: state.setStickyBuyBarIsInView,
  }));
  const { setErrorBannerNotification } = useErrorNotificationStore();
  const isClientSide = useIsClientSide();

  const { variations } = props;

  const {
    addToCartAddedLabel,
    addToCartErrorLabel,
    backOrderText,
    basketEndpoint,
    basketLink,
    buyNowLabel,
    buyNowUrl,
    code,
    dealerFindLink,
    deliveryLabel,
    disableFindDealerCta,
    getProductDetailsByMaterialCodeEndpoint,
    getRelationsByMaterialCodeEndpoint,
    getDynamicContentByMaterialCodeEndpoint,
    language,
    hidePriceFromSavePill,
    isRefurbished,
    maxBasketQuantityText,
    maxBasketValueText,
    maxProductQuantityText,
    mixedCartText,
    name,
    newProductLabel,
    notifyMeContactFormEndpoint,
    overviewLink,
    preOrderText,
    pricingData,
    productImages,
    refurbishedIcon,
    refurbishedLabel,
    savePriceLabel,
    skuCode,
    statusLabels,
    trackingData,
  } = productDetails;

  const { formattedAmountSaved, amountSaved, isCommerceEnabled } =
    pricingData || {};

  const {
    isAddingToCartFromStickyBuyBar,
    isAddToCartLoading,
    isAddToCartTrayOpen,
    addToCartTrayPayload,
    isAddToCartModalOpen,
    addToCartModalContent,
    setIsAddToCartTrayOpen,
    setIsAddToCartModalOpen,
    handleAddProductToCart,
  } = useProductDetailsAddToCart({
    addToCartAddedLabel,
    addToCartErrorLabel,
    basketEndpoint,
    basketLink,
    code,
    isCommerceEnabled,
    maxBasketQuantityText,
    maxBasketValueText,
    maxProductQuantityText,
    mixedCartText,
    skuCode,
    trackingData,
  });

  const stickyOffset = useStickyOffset();
  const isMobile = useMediaQuery(1024);
  const { ctaContainerRef, isCtaContainerAboveViewport } =
    useProductDetailsScroll();
  const { setProductPageContent } = useProductPageContentStore();

  const showSaveTag = savePriceLabel && formattedAmountSaved && amountSaved > 0;

  useEffect(() => {
    setStickyBuyBarIsInView(isMobile === false || isCtaContainerAboveViewport);
  }, [isCtaContainerAboveViewport, isMobile, setStickyBuyBarIsInView]);

  useEffect(() => {
    // On mount of the PDP we want to store the current product code in the browser history state
    // This allows us to handle back/forward navigation correctly
    if (!window.history.state?.code) {
      window.history.replaceState(
        { code: productDetails.code },
        '',
        window.location.href
      );
    }

    if (productDetails.trackingData) {
      updateDataLayer({
        event: 'productDetail',
        ecommerce: {
          currency: productDetails.trackingData.currency,
          items: [
            mapToNewTrackingData(productDetails.trackingData, {
              hideIndex: true,
            }),
          ],
        },
      });
    }
  }, [productDetails]);

  const handleGetProductData = (materialCode: string, updateUrl?: boolean) =>
    getProductData({
      materialCode,
      updateUrl,
      endpoints: {
        getProductDetailsByMaterialCodeEndpoint,
        getRelationsByMaterialCodeEndpoint,
        getDynamicContentByMaterialCodeEndpoint,
      },
      language,
      onSetProductDetails: setProductDetails,
      onSetProductPageContent: setProductPageContent,
      onSetIsLoading: setIsProductDetailsLoading,
      onSetErrorBannerNotification: setErrorBannerNotification,
    });

  // This effect is used to handle the browser's back/forward navigation
  // It listens for popstate events and retrieves the product data based on the code in the history state
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const handlePopState = async (event: PopStateEvent) => {
      const productCode = event.state?.code;
      if (!productCode) return;

      await handleGetProductData(productCode);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <section
      className="relative bg-white pb-20 text-black-100 lg:pb-[112px]"
      data-testid="CT-15-Product-Details"
    >
      <LoadingOverlay isLoading={isAddToCartLoading} />

      {isProductDetailsLoading && !isMobile && (
        <div
          style={{
            paddingTop: galleryContainerHeight / 2,
            paddingBottom: galleryContainerHeight / 2 + 112,
          }}
          className="absolute z-[1] h-full w-full"
        >
          <div className="-translate-y-1/2 sticky top-1/2">
            <LoadingSpinner />
          </div>
        </div>
      )}

      <div
        className={classNames('container lg:grid lg:grid-cols-12', {
          'pointer-events-none opacity-50': isProductDetailsLoading,
        })}
      >
        <div className="py-5 lg:hidden">
          {overviewLink?.name && overviewLink?.url && (
            <ReturnLink
              link={overviewLink}
              dataTestId="CT-15-Product-Details-Return-Link"
            />
          )}
        </div>

        <div className="mb-7.5 lg:col-span-7 lg:col-start-1 lg:mb-0">
          <div
            ref={galleryContainerRef}
            className="relative lg:sticky"
            style={{
              top: isMobile ? 0 : stickyOffset + 10,
            }}
          >
            {(isRefurbished || showSaveTag || newProductLabel) && (
              <div className="absolute top-2.5 z-[1] flex flex-row flex-wrap items-center gap-2.5 max-lg:right-2.5 max-lg:left-2.5 lg:px-2.5 xl:top-[25px] xl:px-5">
                {newProductLabel && (
                  <Tag
                    tagType={TagTypesEnum.Three}
                    pointerStates={false}
                    text={newProductLabel}
                    dataTestId="CT-15-Product-Details-New-Tag"
                  />
                )}

                {isRefurbished && (
                  <Tag
                    pointerStates={false}
                    tagType={TagTypesEnum.Three}
                    dataTestId="CT-15-Product-Details-Refurbished-Tag"
                  >
                    <div className="flex flex-row items-center gap-x-2.5">
                      {refurbishedIcon && (
                        <Icon
                          name={refurbishedIcon}
                          colorClassName="text-yellow"
                          size={IconSizeEnum.md}
                        />
                      )}
                      {refurbishedLabel}
                    </div>
                  </Tag>
                )}

                {showSaveTag && (
                  <Tag
                    tagType={TagTypesEnum.Secondary}
                    pointerStates={false}
                    text={`${savePriceLabel}${
                      hidePriceFromSavePill ? '' : `: ${formattedAmountSaved}`
                    }`}
                    dataTestId="CT-15-Product-Details-Save-Tag"
                  />
                )}
              </div>
            )}

            <ProductDetailsGallery productImages={productImages} />
          </div>
        </div>

        <div className="lg:col-span-4 lg:col-start-9">
          <ProductDetailsSidePanel
            {...productDetails}
            ctaContainerRef={ctaContainerRef}
            isProductDetailsLoading={isProductDetailsLoading}
            variations={variations}
            dataTestId="CT-15-Product-Details"
            onAddProductToCart={async () => {
              await handleAddProductToCart(false);
            }}
            onGetProductData={handleGetProductData}
          />
        </div>
      </div>

      {addToCartTrayPayload && (
        <AddToCartTray
          addToCartErrorLabel={addToCartErrorLabel}
          isAddingToCartFromStickyBuyBar={isAddingToCartFromStickyBuyBar}
          isOpen={isAddToCartTrayOpen}
          mainItemTrackingData={trackingData}
          onSetIsOpen={setIsAddToCartTrayOpen}
          {...addToCartTrayPayload}
        />
      )}

      <InfoModal
        type={addToCartModalContent.type}
        isModalOpen={isAddToCartModalOpen}
        toggleModal={setIsAddToCartModalOpen}
        title={addToCartModalContent.title}
        description={addToCartModalContent.description}
        ctaLink={addToCartModalContent.ctaLink}
        shouldLockBody={!isAddToCartTrayOpen}
        dataTestId="CT-15-Product-Details-Add-To-Cart-Modal"
      />

      {/* z-index for this is set to 71 in order to be displayed under the header (which has z-index 80) 
      but above the footer (with z-index 70)*/}
      <motion.div
        initial={{
          y: '100%',
        }}
        animate={
          isMobile === false || isCtaContainerAboveViewport ? { y: 0 } : {}
        }
        transition={{ ease: 'easeOut', duration: 0.5 }}
        className="fixed right-0 bottom-0 left-0 z-[71]"
      >
        <ProductDetailsStickyBuyBar
          backOrderText={backOrderText}
          buyNowLabel={buyNowLabel}
          buyNowUrl={buyNowUrl}
          className={classNames({
            'pointer-events-none opacity-50': isProductDetailsLoading,
          })}
          dealerFindLink={dealerFindLink}
          deliveryLabel={deliveryLabel}
          disableFindDealerCta={disableFindDealerCta}
          image={productImages[0]}
          name={name}
          notifyMeContactFormEndpoint={notifyMeContactFormEndpoint}
          preOrderText={preOrderText}
          pricingData={pricingData}
          skuCode={skuCode}
          statusLabels={statusLabels}
          trackingData={trackingData}
          onAddProductToCart={async () => {
            await handleAddProductToCart(true);
          }}
        />
      </motion.div>

      {/* To avoid hydration issues, it doesn't make sense to render this component server side */}
      {isClientSide && <PageBackgroundAnimator />}
    </section>
  );
};

export default ProductDetails;
